"use client";

import type { ReactNode } from "react";
import {
  Checkbox,
  CopyButton,
  PillGroup,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import type { CipherTextOptions } from "@/lib/tool-kit/ciphers/alphabet";

/*
 * The shared frame for a cipher widget: encode/decode pills, a slot for the key, the
 * input, keep/strip checkboxes, the live output with Copy / Swap / Clear, and a "how it
 * works" panel for the working.
 *
 * Controlled, and vocabulary-free. The widget owns every piece of state (mode, input,
 * key, options), computes the output itself, and passes every visible word in through
 * `labels` from its own `<SLUG>_STRINGS` — the frame owns layout and behaviour only, so
 * a translated content file still reaches every label. Pure cipher logic belongs in
 * `src/lib/tool-kit/ciphers/`, where `applyTextOptions` implements the checkboxes.
 *
 *     const [mode, setMode] = useState<CipherMode>("encode");
 *     const [input, setInput] = useState(strings.sample);
 *     const [options, setOptions] = useState(KEEP_ALL_TEXT);
 *     const output = applyTextOptions(myCipher(input, key, mode), options);
 *     return (
 *       <CipherWorkbench
 *         idPrefix="hil"
 *         input={input}
 *         keyControls={<TextField id="hil-key" … />}
 *         labels={{ mode: strings.mode, encode: strings.encode, … }}
 *         mode={mode}
 *         onInputChange={setInput}
 *         onModeChange={setMode}
 *         output={output}
 *         textOptions={{ value: options, onChange: setOptions, show: ["keepCase"] }}
 *         working={<DataTable … />}
 *       />
 *     );
 *
 * A self-inverse cipher (Atbash) omits `mode` and `onModeChange`: no pills, no Swap.
 * Ids are `${idPrefix}-input`, `-output` and `-mode`; key controls choose their own ids
 * with the same prefix. Every slot is optional and renders nothing when absent.
 *
 * `CipherGrid` (key squares, zigzags) and `SymbolChart` (reference alphabets) below are
 * the two displays most ciphers need for their working.
 */

export type CipherMode = "encode" | "decode";

export type CipherTextOptionKey = keyof CipherTextOptions;

export interface CipherWorkbenchLabels {
  /** The pills' legend and labels. Needed only when the widget passes `mode`. */
  mode?: string;
  encode?: string;
  decode?: string;
  /** The input box, already worded for the current mode ("Plain text"). */
  input: string;
  inputPlaceholder?: string;
  /** The output box, already worded for the current mode ("Cipher text"). */
  output: string;
  /** Shown in the empty output box. */
  outputPlaceholder?: string;
  /** The keep/strip legend, and a label for each checkbox in `textOptions.show`. */
  textOptions?: string;
  keepCase?: string;
  keepSpaces?: string;
  keepPunctuation?: string;
  copy: string;
  copied: string;
  clear: string;
  /** Puts the output into the input and flips the mode. Omit to hide the button. */
  swap?: string;
  /** The heading over `working`. */
  working?: string;
}

export interface CipherWorkbenchProps {
  /** A short prefix for element ids, unique on the page ("atb", "pf"). */
  idPrefix: string;
  labels: CipherWorkbenchLabels;
  mode?: CipherMode;
  onModeChange?: (mode: CipherMode) => void;
  input: string;
  onInputChange: (value: string) => void;
  /** The plain-text result — what Copy copies and Swap moves. */
  output: string;
  /** Drawn instead of the read-only output box (to mark letters, say). */
  outputView?: ReactNode;
  /** The key: keyword, shift, rails, alphabet choice… Between the pills and the input. */
  keyControls?: ReactNode;
  textOptions?: {
    value: CipherTextOptions;
    onChange: (next: CipherTextOptions) => void;
    show: readonly CipherTextOptionKey[];
  };
  /** Under the input: validation, characters that were skipped. */
  notices?: ReactNode;
  /** Under the output: how to read it ("remove the filler X by hand"). */
  outputNotes?: ReactNode;
  /** Panels after the output — a brute-force list, a demonstration. */
  extras?: ReactNode;
  /** The step-by-step working, in a panel headed `labels.working`. */
  working?: ReactNode;
}

function TextOptionBoxes({
  labels,
  textOptions,
}: {
  labels: CipherWorkbenchLabels;
  textOptions: NonNullable<CipherWorkbenchProps["textOptions"]>;
}) {
  const { value, onChange, show } = textOptions;
  return (
    <fieldset className="space-y-2">
      <legend className={LABEL}>{labels.textOptions}</legend>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {show.map((key) => (
          <Checkbox
            checked={value[key]}
            key={key}
            label={labels[key] ?? key}
            onChange={(checked) => onChange({ ...value, [key]: checked })}
          />
        ))}
      </div>
    </fieldset>
  );
}

export function CipherWorkbench({
  idPrefix,
  labels,
  mode,
  onModeChange,
  input,
  onInputChange,
  output,
  outputView,
  keyControls,
  textOptions,
  notices,
  outputNotes,
  extras,
  working,
}: CipherWorkbenchProps) {
  const inputId = `${idPrefix}-input`;
  const outputId = `${idPrefix}-output`;
  const modes =
    mode !== undefined && onModeChange !== undefined && labels.mode
      ? { value: mode, onChange: onModeChange, legend: labels.mode }
      : null;

  function swap() {
    if (!modes) {
      return;
    }
    onInputChange(output);
    modes.onChange(modes.value === "encode" ? "decode" : "encode");
  }

  return (
    <>
      {modes || keyControls ? (
        <section className="space-y-5">
          {modes ? (
            <PillGroup
              label={modes.legend}
              name={`${idPrefix}-mode`}
              onChange={modes.onChange}
              options={[
                { value: "encode", label: labels.encode ?? "" },
                { value: "decode", label: labels.decode ?? "" },
              ]}
              value={modes.value}
            />
          ) : null}
          {keyControls}
        </section>
      ) : null}

      <section className="space-y-3">
        <label className={LABEL} htmlFor={inputId}>
          {labels.input}
        </label>
        <textarea
          className={TEXTAREA}
          id={inputId}
          onChange={(event) => onInputChange(event.target.value)}
          placeholder={labels.inputPlaceholder}
          spellCheck={false}
          value={input}
        />
        {textOptions ? (
          <TextOptionBoxes labels={labels} textOptions={textOptions} />
        ) : null}
        {notices}
      </section>

      <section className="space-y-3">
        {outputView ? (
          <p className={LABEL} id={`${outputId}-label`}>
            {labels.output}
          </p>
        ) : (
          <label className={LABEL} htmlFor={outputId}>
            {labels.output}
          </label>
        )}
        <div aria-live="polite">
          {outputView ? (
            <section aria-labelledby={`${outputId}-label`} id={outputId}>
              {outputView}
            </section>
          ) : (
            <textarea
              className={TEXTAREA}
              id={outputId}
              placeholder={labels.outputPlaceholder}
              readOnly
              spellCheck={false}
              value={output}
            />
          )}
        </div>
        {outputNotes}
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={labels.copied}
            label={labels.copy}
            text={output}
          />
          {modes && labels.swap ? (
            <button
              className={BUTTON_SECONDARY}
              disabled={output.length === 0}
              onClick={swap}
              type="button"
            >
              {labels.swap}
            </button>
          ) : null}
          <button
            className={BUTTON_SECONDARY}
            disabled={input.length === 0}
            onClick={() => onInputChange("")}
            type="button"
          >
            {labels.clear}
          </button>
        </div>
      </section>

      {extras}

      {working ? (
        <section className={`${CARD} space-y-4`}>
          {labels.working ? (
            <h2 className={WIDGET_H2}>{labels.working}</h2>
          ) : null}
          {working}
        </section>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- displays */

/**
 * How a grid cell is marked: `primary` (blue) for the letters going in, `secondary`
 * (amber) for the letters coming out. Named hues rather than theme tokens, because a
 * widget's hint may need to say which colour is which — the site's `primary` is
 * near-black.
 */
export type GridTone = "primary" | "secondary" | "muted";

const GRID_TONES: Record<GridTone, string> = {
  primary:
    "border-sky-500 bg-sky-200/70 font-semibold text-foreground dark:bg-sky-400/30",
  secondary:
    "border-amber-500 bg-amber-200/70 font-semibold text-foreground dark:bg-amber-400/30",
  muted: "border-border bg-muted/40 text-muted-foreground",
};

const GRID_CELL = "border text-center font-mono uppercase transition-colors";
const GRID_HEAD =
  "whitespace-nowrap px-2 text-center font-medium text-muted-foreground text-xs";

/**
 * A grid of letters — a 5 × 5 key square, a zigzag of rails — as a real table, so a
 * screen reader can move by row and column. `cells` is row-major; `null` is an empty
 * cell. `tone` marks cells by index (a pair being enciphered, the letters in use).
 */
export function CipherGrid({
  cells,
  columns,
  label,
  rowHeaders,
  columnHeaders,
  tone,
  dense = false,
}: {
  cells: readonly (string | null)[];
  columns: number;
  /** The table's accessible name, e.g. "Key square". */
  label: string;
  rowHeaders?: readonly string[];
  columnHeaders?: readonly string[];
  tone?: (index: number) => GridTone | undefined;
  /** Small cells for long grids (a rail fence); scrolls sideways when wide. */
  dense?: boolean;
}) {
  const width = Math.max(1, columns);
  const rows: (string | null)[][] = [];
  for (let start = 0; start < cells.length; start += width) {
    rows.push(cells.slice(start, start + width));
  }
  const size = dense
    ? "h-6 w-6 min-w-6 text-xs"
    : "h-11 w-11 text-lg sm:h-12 sm:w-12";
  return (
    <div className="overflow-x-auto">
      <table
        aria-label={label}
        className={`border-separate ${dense ? "border-spacing-0.5" : "border-spacing-1"}`}
      >
        {columnHeaders ? (
          <thead>
            <tr>
              {rowHeaders ? <td /> : null}
              {columnHeaders.map((header) => (
                <th className={GRID_HEAD} key={header} scope="col">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {rows.map((row, rowIndex) => (
            // Rows are positions in the grid; they have no identity of their own.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
            <tr key={rowIndex}>
              {rowHeaders ? (
                <th className={GRID_HEAD} scope="row">
                  {rowHeaders[rowIndex]}
                </th>
              ) : null}
              {row.map((cell, columnIndex) => {
                const index = rowIndex * width + columnIndex;
                const marked = tone?.(index);
                const toneClass = marked
                  ? GRID_TONES[marked]
                  : "border-border bg-card text-foreground";
                return (
                  <td
                    className={`${GRID_CELL} ${size} ${cell === null ? "border-transparent" : toneClass}`}
                    // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                    key={columnIndex}
                  >
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface SymbolChartItem {
  /** What is looked up — usually a letter. Must be unique within the chart. */
  key: string;
  /** What it becomes: a letter, a number, a code. */
  value: ReactNode;
  /** Marks an entry the current message uses. */
  active?: boolean;
}

/** A reference alphabet as a grid of small cards: A → 1, A ↔ Z, A → aaaaa. */
export function SymbolChart({
  items,
  label,
  wide = false,
}: {
  items: readonly SymbolChartItem[];
  /** The list's accessible name, e.g. "Letter numbers". */
  label: string;
  /** Fewer, wider columns for long values such as five-symbol codes. */
  wide?: boolean;
}) {
  const columns = wide
    ? "grid-cols-3 sm:grid-cols-4 md:grid-cols-6"
    : "grid-cols-4 sm:grid-cols-7 md:grid-cols-9";
  return (
    <ul aria-label={label} className={`grid gap-2 ${columns}`}>
      {items.map((item) => (
        <li
          className={`rounded-lg border p-2 text-center ${
            item.active
              ? "border-primary/50 bg-primary/10"
              : "border-border bg-card"
          }`}
          key={item.key}
        >
          <p className="font-semibold text-foreground">{item.key}</p>
          <p className="mt-0.5 font-mono text-muted-foreground text-xs">
            {item.value}
          </p>
        </li>
      ))}
    </ul>
  );
}
